import { subscriptNumber, type ChoiceQuestion } from './polymersBiomolecules';
import {
  buildTriglyceride,
  FATTY_ACIDS,
  FATTY_ACIDS_PER_TRIGLYCERIDE,
  GLYCEROL,
  type FattyAcid,
} from './polymersBiomoleculesBio';

export const SOAP_DATA_META = {
  sources: [
    'IUPAC Compendium of Chemical Terminology (Gold Book): saponification, surfactant, micelle and critical micelle concentration',
    'NIST Chemistry WebBook: molecular formulae of fatty acids, glycerol, sodium stearate and sodium dodecyl sulfate',
    'Standard senior chemistry texts: soap and synthetic detergent structure and behaviour in hard water',
  ],
  conditions:
    'Structures and molecular formulae only. Saponification is modelled as complete reaction with excess hot aqueous alkali; hard-water behaviour refers to typical domestic hardness at about 25 °C.',
  reviewStatus: 'pending-teaching-review',
} as const;

// ---------------------------------------------------------------------------------------------
// Atom counting (wider element set than the polymer module: metals for salts)
// ---------------------------------------------------------------------------------------------

export type SoapElement = 'C' | 'H' | 'O' | 'S' | 'Na' | 'K' | 'Ca' | 'Mg';
export type SoapCounts = Partial<Record<SoapElement, number>>;

const SOAP_FORMULA_ORDER: readonly SoapElement[] = ['C', 'H', 'Ca', 'K', 'Mg', 'Na', 'O', 'S'];

export function addCounts(...parts: Array<{ counts: SoapCounts; times?: number }>): SoapCounts {
  const total: SoapCounts = {};
  for (const { counts, times = 1 } of parts) {
    for (const element of SOAP_FORMULA_ORDER) {
      const value = (counts[element] ?? 0) * times;
      if (value !== 0) total[element] = (total[element] ?? 0) + value;
    }
  }
  for (const element of SOAP_FORMULA_ORDER) {
    if (total[element] === 0) delete total[element];
  }
  return total;
}

export function soapCountsEqual(left: SoapCounts, right: SoapCounts): boolean {
  return SOAP_FORMULA_ORDER.every((element) => (left[element] ?? 0) === (right[element] ?? 0));
}

export function formatSoapFormula(counts: SoapCounts): string {
  return SOAP_FORMULA_ORDER.filter((element) => (counts[element] ?? 0) > 0)
    .map((element) => {
      const count = counts[element]!;
      return `${element}${count === 1 ? '' : subscriptNumber(count)}`;
    })
    .join('');
}

export const BALANCE_ELEMENTS = SOAP_FORMULA_ORDER;

// ---------------------------------------------------------------------------------------------
// Saponification
// ---------------------------------------------------------------------------------------------

export type Alkali = 'NaOH' | 'KOH';
export const ALKALIS: readonly Alkali[] = ['NaOH', 'KOH'];

const ALKALI_METAL: Record<Alkali, { symbol: 'Na' | 'K'; name: 'sodium' | 'potassium' }> = {
  NaOH: { symbol: 'Na', name: 'sodium' },
  KOH: { symbol: 'K', name: 'potassium' },
};

/** Carboxylate names for the fatty acids that the biomolecules mode already reviews. */
const CARBOXYLATE_NAMES: Record<string, string> = {
  palmitic: 'palmitate',
  stearic: 'stearate',
  oleic: 'oleate',
  linoleic: 'linoleate',
};

export const SOAP_FATS = FATTY_ACIDS.filter((acid) => acid.id in CARBOXYLATE_NAMES);

export interface Saponification {
  fattyAcid: FattyAcid;
  alkali: Alkali;
  triglycerideName: string;
  triglycerideFormula: string;
  soapName: string;
  /** Condensed ionic formula, e.g. C₁₇H₃₅COO⁻Na⁺. */
  soapIonic: string;
  soapCounts: SoapCounts;
  soapFormula: string;
  hydroxidePerFat: number;
  equation: string;
  reactantCounts: SoapCounts;
  productCounts: SoapCounts;
  balanced: boolean;
}

export type SaponificationResult = { ok: true; value: Saponification } | { ok: false; message: string };

/** Triglyceride + 3 MOH → glycerol + 3 RCOO⁻M⁺. Fails closed for an unreviewed fat or alkali. */
export function saponify(fattyAcidId: string, alkali: string): SaponificationResult {
  const fattyAcid = SOAP_FATS.find((acid) => acid.id === fattyAcidId);
  if (!fattyAcid) return { ok: false, message: 'That fat is not in the reviewed set.' };
  if (!ALKALIS.includes(alkali as Alkali)) {
    return { ok: false, message: 'Only NaOH and KOH saponification is modelled.' };
  }
  const base = alkali as Alkali;
  const triglyceride = buildTriglyceride(fattyAcid.id);
  if (!triglyceride) return { ok: false, message: 'That fat is not in the reviewed set.' };
  const metal = ALKALI_METAL[base];
  const metalCounts: SoapCounts = metal.symbol === 'Na' ? { Na: 1 } : { K: 1 };
  const hydroxide = addCounts({ counts: metalCounts }, { counts: { O: 1, H: 1 } });
  // RCOOH → RCOO⁻M⁺: remove the acidic H, add the metal ion.
  const soapCounts = addCounts({ counts: fattyAcid.counts }, { counts: { H: 1 }, times: -1 }, { counts: metalCounts });
  const reactantCounts = addCounts(
    { counts: triglyceride.counts },
    { counts: hydroxide, times: FATTY_ACIDS_PER_TRIGLYCERIDE },
  );
  const productCounts = addCounts(
    { counts: GLYCEROL },
    { counts: soapCounts, times: FATTY_ACIDS_PER_TRIGLYCERIDE },
  );
  const soapName = `${metal.name} ${CARBOXYLATE_NAMES[fattyAcid.id]}`;
  const soapIonic = `${fattyAcid.chain}COO⁻${metal.symbol}⁺`;
  return {
    ok: true,
    value: {
      fattyAcid,
      alkali: base,
      triglycerideName: triglyceride.name,
      triglycerideFormula: triglyceride.formula,
      soapName,
      soapIonic,
      soapCounts,
      soapFormula: formatSoapFormula(soapCounts),
      hydroxidePerFat: FATTY_ACIDS_PER_TRIGLYCERIDE,
      equation: `${triglyceride.formula} + ${FATTY_ACIDS_PER_TRIGLYCERIDE}${base} → C₃H₈O₃ + ${FATTY_ACIDS_PER_TRIGLYCERIDE}${soapIonic}`,
      reactantCounts,
      productCounts,
      balanced: soapCountsEqual(reactantCounts, productCounts),
    },
  };
}

function rotate<T>(items: T[], by: number): T[] {
  const shift = by % items.length;
  return [...items.slice(shift), ...items.slice(0, shift)];
}

/** Generated prediction question for one fat and alkali. */
export function saponificationQuestion(value: Saponification, seed: number): ChoiceQuestion {
  const acidName = value.fattyAcid.name.split(' (')[0];
  const options = [
    {
      id: 'correct',
      label: `propane-1,2,3-triol (glycerol) + 3 ${value.soapName}`,
      correct: true,
      feedback: `Correct. Each of the three ester links is hydrolysed by one ${value.alkali}, releasing the glycerol backbone and three carboxylate salts (${value.soapIonic}). Those salts are the soap.`,
    },
    {
      id: 'one-soap',
      label: `propane-1,2,3-triol (glycerol) + 1 ${value.soapName}`,
      correct: false,
      feedback: `A triglyceride has three ester links, one for each fatty-acid chain. All three are hydrolysed, so three ${value.soapName} form and ${value.hydroxidePerFat} ${value.alkali} are used. With one salt the atoms do not balance.`,
    },
    {
      id: 'free-acid',
      label: `propane-1,2,3-triol (glycerol) + 3 ${acidName}`,
      correct: false,
      feedback: `Free ${acidName} forms in acid hydrolysis. In excess ${value.alkali} the acid is neutralised at once, so the product is its salt, ${value.soapName}.`,
    },
    {
      id: 'water',
      label: `3 ${value.soapName} + 3 H₂O`,
      correct: false,
      feedback: 'No water is a product here and the glycerol backbone does not disappear. It is released as propane-1,2,3-triol, C₃H₈O₃.',
    },
  ];
  return {
    id: `saponify-${value.fattyAcid.id}-${value.alkali}`,
    prompt: `${value.triglycerideName} (${value.triglycerideFormula}) is boiled with excess ${value.alkali}(aq). What forms?`,
    options: rotate(options, seed),
  };
}

// ---------------------------------------------------------------------------------------------
// Surfactant structure: soap vs synthetic detergent
// ---------------------------------------------------------------------------------------------

export type HeadGroup = 'carboxylate' | 'sulfonate' | 'sulfate';

export const HEAD_GROUP_FORMULA: Record<HeadGroup, string> = {
  carboxylate: '–COO⁻',
  sulfonate: '–SO₃⁻',
  sulfate: '–OSO₃⁻',
};

export interface Surfactant {
  id: string;
  name: string;
  kind: 'soap' | 'synthetic detergent';
  condensed: string;
  /** Carbon atoms in the hydrocarbon tail (a benzene ring counts as six). */
  tailCarbons: number;
  tailNote: string;
  headGroup: HeadGroup;
  counterIon: 'Na⁺' | 'K⁺';
  /** Anion written for the hard-water equation. */
  anion: string;
  /** Neutral group used inside the scum formula, e.g. C₁₇H₃₅COO. */
  anionGroup: string;
  anionCounts: SoapCounts;
}

export const SURFACTANTS: Surfactant[] = [
  {
    id: 'sodium-stearate',
    name: 'sodium stearate',
    kind: 'soap',
    condensed: 'CH₃(CH₂)₁₆COO⁻ Na⁺',
    tailCarbons: 17,
    tailNote: 'saturated C₁₇ chain, common in animal fats such as tallow',
    headGroup: 'carboxylate',
    counterIon: 'Na⁺',
    anion: 'C₁₇H₃₅COO⁻',
    anionGroup: 'C₁₇H₃₅COO',
    anionCounts: { C: 18, H: 35, O: 2 },
  },
  {
    id: 'potassium-oleate',
    name: 'potassium oleate',
    kind: 'soap',
    condensed: 'CH₃(CH₂)₇CH=CH(CH₂)₇COO⁻ K⁺',
    tailCarbons: 17,
    tailNote: 'unsaturated C₁₇ chain with one cis C=C (soft or liquid soap)',
    headGroup: 'carboxylate',
    counterIon: 'K⁺',
    anion: 'C₁₇H₃₃COO⁻',
    anionGroup: 'C₁₇H₃₃COO',
    anionCounts: { C: 18, H: 33, O: 2 },
  },
  {
    id: 'sodium-dodecylbenzenesulfonate',
    name: 'sodium dodecylbenzenesulfonate',
    kind: 'synthetic detergent',
    condensed: 'CH₃(CH₂)₁₁C₆H₄SO₃⁻ Na⁺',
    tailCarbons: 18,
    tailNote: 'C₁₂ chain joined to a benzene ring (alkylbenzene sulfonate)',
    headGroup: 'sulfonate',
    counterIon: 'Na⁺',
    anion: 'C₁₂H₂₅C₆H₄SO₃⁻',
    anionGroup: 'C₁₂H₂₅C₆H₄SO₃',
    anionCounts: { C: 18, H: 29, O: 3, S: 1 },
  },
  {
    id: 'sodium-dodecyl-sulfate',
    name: 'sodium dodecyl sulfate (sodium lauryl sulfate)',
    kind: 'synthetic detergent',
    condensed: 'CH₃(CH₂)₁₁OSO₃⁻ Na⁺',
    tailCarbons: 12,
    tailNote: 'saturated C₁₂ chain (alkyl sulfate)',
    headGroup: 'sulfate',
    counterIon: 'Na⁺',
    anion: 'C₁₂H₂₅OSO₃⁻',
    anionGroup: 'C₁₂H₂₅OSO₃',
    anionCounts: { C: 12, H: 25, O: 4, S: 1 },
  },
];

export function surfactantById(id: string): Surfactant | null {
  return SURFACTANTS.find((item) => item.id === id) ?? null;
}

// ---------------------------------------------------------------------------------------------
// Micelle generated from data
// ---------------------------------------------------------------------------------------------

export const MICELLE_MOLECULES = 14;
const MICELLE_CORE_RADIUS = 34;
const TAIL_LENGTH_PER_CARBON = 2.6;

export interface MicelleMolecule {
  angle: number;
  /** Inner end of the tail, in the oil droplet. */
  tailX: number;
  tailY: number;
  /** Head group, in the water. */
  headX: number;
  headY: number;
}

export interface MicelleLayout {
  coreRadius: number;
  headRadius: number;
  molecules: MicelleMolecule[];
}

/** Tails point into the oil droplet, ionic heads sit on the outside in water. */
export function micelleLayout(surfactant: Surfactant, centreX: number, centreY: number): MicelleLayout {
  const headRadius = MICELLE_CORE_RADIUS + surfactant.tailCarbons * TAIL_LENGTH_PER_CARBON;
  const innerRadius = MICELLE_CORE_RADIUS * 0.35;
  const molecules = Array.from({ length: MICELLE_MOLECULES }, (_, index) => {
    const angle = (2 * Math.PI * index) / MICELLE_MOLECULES;
    return {
      angle,
      tailX: centreX + innerRadius * Math.cos(angle),
      tailY: centreY + innerRadius * Math.sin(angle),
      headX: centreX + headRadius * Math.cos(angle),
      headY: centreY + headRadius * Math.sin(angle),
    };
  });
  return { coreRadius: MICELLE_CORE_RADIUS + surfactant.tailCarbons * TAIL_LENGTH_PER_CARBON * 0.6, headRadius, molecules };
}

export type MicellePart = 'tail' | 'head';

export function checkMicellePrediction(intoOil: MicellePart) {
  return intoOil === 'tail'
    ? {
        correct: true,
        feedback: 'Correct. The non-polar hydrocarbon tails dissolve in the oil by dispersion forces. The ionic heads stay in the water, where ion–dipole attraction holds them. The charged surfaces of micelles repel each other, so the oil stays suspended and rinses away.',
      }
    : {
        correct: false,
        feedback: 'The ionic head is attracted to polar water molecules (ion–dipole), not to non-polar oil. The hydrocarbon tail is the part that dissolves in the oil, so the tails point into the droplet and the heads face the water.',
      };
}

// ---------------------------------------------------------------------------------------------
// Hard water
// ---------------------------------------------------------------------------------------------

export type WaterType = 'soft' | 'hard-calcium' | 'hard-magnesium';

export const WATER_TYPES: { id: WaterType; label: string; ion: 'Ca' | 'Mg' | null }[] = [
  { id: 'soft', label: 'Soft (rain) water', ion: null },
  { id: 'hard-calcium', label: 'Hard water with Ca²⁺', ion: 'Ca' },
  { id: 'hard-magnesium', label: 'Hard water with Mg²⁺', ion: 'Mg' },
];

export interface HardWaterOutcome {
  scum: boolean;
  equation: string | null;
  scumFormula: string | null;
  scumCounts: SoapCounts | null;
  explanation: string;
}

export function hardWaterOutcome(surfactantId: string, water: string): HardWaterOutcome | null {
  const surfactant = surfactantById(surfactantId);
  const waterType = WATER_TYPES.find((item) => item.id === water);
  if (!surfactant || !waterType) return null;
  if (waterType.ion === null) {
    return {
      scum: false,
      equation: null,
      scumFormula: null,
      scumCounts: null,
      explanation: `Soft water has very few Ca²⁺ or Mg²⁺ ions, so ${surfactant.name} stays dissolved and lathers.`,
    };
  }
  const ion = waterType.ion;
  if (surfactant.headGroup === 'carboxylate') {
    const ionCounts: SoapCounts = ion === 'Ca' ? { Ca: 1 } : { Mg: 1 };
    const scumCounts = addCounts({ counts: surfactant.anionCounts, times: 2 }, { counts: ionCounts });
    return {
      scum: true,
      equation: `2${surfactant.anion}(aq) + ${ion}²⁺(aq) → (${surfactant.anionGroup})₂${ion}(s)`,
      scumFormula: `(${surfactant.anionGroup})₂${ion}`,
      scumCounts,
      explanation: `${ion === 'Ca' ? 'Calcium' : 'Magnesium'} carboxylates are insoluble. Two soap anions are removed for each ${ion}²⁺ as a grey scum, so soap is wasted until the hardness ions are used up.`,
    };
  }
  return {
    scum: false,
    equation: null,
    scumFormula: null,
    scumCounts: null,
    explanation: `The ${surfactant.headGroup} head is the anion of a strong acid. Its ${ion === 'Ca' ? 'calcium' : 'magnesium'} salt is soluble, so no scum forms and the detergent keeps cleaning in hard water.`,
  };
}

export function checkScumPrediction(surfactantId: string, water: string, predictsScum: boolean) {
  const outcome = hardWaterOutcome(surfactantId, water);
  if (!outcome) return null;
  const correct = outcome.scum === predictsScum;
  return {
    correct,
    outcome,
    feedback: `${correct ? 'Correct' : outcome.scum ? 'Scum does form' : 'No scum forms'}. ${outcome.explanation}`,
  };
}

// ---------------------------------------------------------------------------------------------
// Question bank
// ---------------------------------------------------------------------------------------------

export const SOAP_QUESTIONS: ChoiceQuestion[] = [
  {
    id: 'saponification-bond',
    prompt: 'Which bonds in a fat are broken during saponification?',
    options: [
      { id: 'ester', label: 'The three ester links –COO–', correct: true, feedback: 'Correct. Hydroxide ions hydrolyse each ester link, giving glycerol and three carboxylate ions.' },
      { id: 'cc', label: 'C=C double bonds in the chains', correct: false, feedback: 'C=C bonds are unchanged; saturated fats make soap too. Saponification hydrolyses the ester links between glycerol and the fatty acids.' },
      { id: 'ch', label: 'C–H bonds in the tails', correct: false, feedback: 'The hydrocarbon tails pass into the soap unchanged. Only the ester links –COO– are hydrolysed.' },
    ],
  },
  {
    id: 'tail-property',
    prompt: 'Why does the long hydrocarbon tail of a soap anion dissolve in grease?',
    options: [
      { id: 'ionic', label: 'It carries a negative charge', correct: false, feedback: 'The charge is on the head group, not the tail. A charged group is attracted to water, not to grease.' },
      { id: 'nonpolar', label: 'It is non-polar, like the grease', correct: true, feedback: 'Correct. Non-polar tails and non-polar grease attract by dispersion forces, so the tail mixes into the grease.' },
      { id: 'hbond', label: 'It forms hydrogen bonds with the grease', correct: false, feedback: 'A C–H tail cannot hydrogen bond. Tail and grease are both non-polar and attract by dispersion forces.' },
    ],
  },
  {
    id: 'detergent-head',
    prompt: 'What head group does sodium dodecylbenzenesulfonate have?',
    options: [
      { id: 'carboxylate', label: 'Carboxylate –COO⁻', correct: false, feedback: 'Carboxylate heads belong to soaps made from fats. This synthetic detergent has a sulfonate head, –SO₃⁻, bonded to a benzene ring.' },
      { id: 'sulfonate', label: 'Sulfonate –SO₃⁻', correct: true, feedback: 'Correct. The sulfonate head is joined directly to carbon of the benzene ring. Its calcium salt is soluble, so it works in hard water.' },
      { id: 'hydroxyl', label: 'Hydroxyl –OH', correct: false, feedback: 'A hydroxyl group alone is not an ionic head. The detergent has an ionic sulfonate head, –SO₃⁻.' },
    ],
  },
  {
    id: 'micelle-heads',
    prompt: 'In a micelle around an oil droplet in water, where are the ionic heads?',
    options: [
      { id: 'outside', label: 'On the outer surface, in the water', correct: true, feedback: 'Correct. Heads face the water; tails point into the oil. The negative surfaces of neighbouring micelles repel, so droplets do not join again.' },
      { id: 'inside', label: 'Inside the oil droplet', correct: false, feedback: 'Ionic heads are not attracted to non-polar oil. The tails go into the oil and the heads stay in the water.' },
      { id: 'random', label: 'Spread randomly through the droplet', correct: false, feedback: 'The arrangement is not random: like attracts like. Non-polar tails go into the oil and the ionic heads face the water.' },
    ],
  },
  {
    id: 'scum-cause',
    prompt: 'Why does soap form scum in hard water?',
    options: [
      { id: 'precipitate', label: 'Ca²⁺ and Mg²⁺ form insoluble carboxylate salts with soap anions', correct: true, feedback: 'Correct. For example 2C₁₇H₃₅COO⁻(aq) + Ca²⁺(aq) → (C₁₇H₃₅COO)₂Ca(s). The soap is removed as scum.' },
      { id: 'sodium', label: 'Na⁺ ions in hard water precipitate the soap', correct: false, feedback: 'Sodium salts are soluble; soap is itself a sodium salt. Hardness comes from Ca²⁺ and Mg²⁺, whose carboxylates are insoluble.' },
      { id: 'grease', label: 'Hard water contains more grease', correct: false, feedback: 'Hardness is about dissolved Ca²⁺ and Mg²⁺ ions, not grease. These ions precipitate soap anions as scum.' },
    ],
  },
  {
    id: 'detergent-hard-water',
    prompt: 'Why do synthetic detergents still lather in hard water?',
    options: [
      { id: 'soluble', label: 'Their calcium and magnesium salts are soluble', correct: true, feedback: 'Correct. Sulfonate and sulfate heads do not precipitate with Ca²⁺ or Mg²⁺, so no scum forms and no detergent is wasted.' },
      { id: 'no-charge', label: 'Detergent anions have no charge', correct: false, feedback: 'These detergents are anionic: the head carries a negative charge, like soap. The difference is that their Ca²⁺ and Mg²⁺ salts are soluble.' },
      { id: 'remove', label: 'Detergents remove Ca²⁺ by precipitating it', correct: false, feedback: 'If detergents precipitated Ca²⁺ they would form scum like soap. Their calcium and magnesium salts stay dissolved.' },
    ],
  },
  {
    id: 'potassium-soap',
    prompt: 'A soap maker uses KOH instead of NaOH with the same oil. What changes?',
    options: [
      { id: 'softer', label: 'A softer, more soluble soap, often a liquid soap', correct: true, feedback: 'Correct. Potassium carboxylates are more soluble and softer than sodium carboxylates, so KOH is used for liquid and soft soaps.' },
      { id: 'no-soap', label: 'No soap forms, because KOH is a weaker base', correct: false, feedback: 'KOH is a strong base, like NaOH. It saponifies fats to potassium carboxylates, which are softer soaps.' },
      { id: 'detergent', label: 'A synthetic detergent forms', correct: false, feedback: 'Any fat plus alkali gives a carboxylate salt, which is a soap. Detergents have sulfonate or sulfate heads made from petrochemicals.' },
    ],
  },
  {
    id: 'glycerol-byproduct',
    prompt: 'What is the other product when a fat is saponified, besides soap?',
    options: [
      { id: 'glycerol', label: 'Glycerol, propane-1,2,3-triol', correct: true, feedback: 'Correct. The three-carbon backbone of the triglyceride is released as glycerol, C₃H₈O₃, which has three –OH groups.' },
      { id: 'ethanol', label: 'Ethanol', correct: false, feedback: 'Ethanol is not part of a fat. The backbone of a triglyceride is glycerol, released as propane-1,2,3-triol.' },
      { id: 'water', label: 'Water', correct: false, feedback: 'Water forms when a fat is made (condensation), not when it is saponified. Saponification releases glycerol.' },
    ],
  },
];
